import classNames from 'classnames';
import React, { Ref, useCallback, useId, useState, JSX, useMemo } from 'react';
import TextAreaAutosize, { TextareaAutosizeProps } from 'react-textarea-autosize';

import '@eika/expandable-input-style';
import { IconTypes } from '@eika/icon';
import { InputLabel } from '@eika/input';
import { ErrorTag } from '@eika/tag';

export interface ExpandableInputProps extends TextareaAutosizeProps {
    className?: string;
    placeholderText?: string;
    showCharCounter?: boolean;
    error?: React.ReactNode;
    label?: React.ReactNode;
    subLabel?: React.ReactNode;
    errorIcon?: IconTypes;
}

const ExpandableInput = React.forwardRef(
    (
        {
            className,
            placeholderText,
            onChange,
            maxLength,
            showCharCounter = false,
            error = false,
            label,
            subLabel,
            id,
            errorIcon,
            ...rest
        }: ExpandableInputProps,
        ref: Ref<HTMLTextAreaElement>,
    ): JSX.Element => {
        const [hasFocus, setHasFocus] = useState(false);
        const [charLength, setCharLength] = useState(0);

        const generatedId = useId();
        const targetId = id || generatedId;

        const limitSize = useCallback(
            (event: React.ChangeEvent<HTMLTextAreaElement>) => {
                const newValue = event.target.value.substring(0, maxLength);
                onChange?.({
                    ...event,
                    target: { ...event.target, value: newValue },
                });
            },
            [maxLength, onChange],
        );

        const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
            if (typeof maxLength === 'number') {
                limitSize(event);
            } else {
                onChange?.(event);
            }
            setCharLength(event.target.value.length);
        };

        const charCountScreenReaderText = useMemo(() => {
            if (typeof maxLength !== 'number' || !showCharCounter) return '';
            if (maxLength < charLength) return `Antall tegn er over maks lengde på ${maxLength}`;
            if (maxLength === charLength) return `Maks antall tegn nådd: ${charLength}/${maxLength}`;
            return `Gjennstående tegn: ${maxLength - charLength}`;
        }, [charLength, maxLength, showCharCounter]);

        return (
            <div className="sgw-expandable-input__wrapper">
                <InputLabel
                    htmlFor={targetId}
                    label={label}
                    sublabel={subLabel}
                    className="sgw-expandable-input__label"
                />
                <TextAreaAutosize
                    id={targetId}
                    className={classNames(
                        'sgw-expandable-input',
                        { 'sgw-expandable-input__error': !!error },
                        className,
                    )}
                    placeholder={hasFocus ? '' : placeholderText}
                    onFocus={() => setHasFocus(true)}
                    onBlur={() => setHasFocus(false)}
                    maxLength={maxLength}
                    onChange={handleChange}
                    aria-invalid={!!error}
                    ref={ref}
                    aria-describedby={`${targetId}-error`}
                    aria-controls={`${targetId}-char-counter`}
                    {...rest}
                />

                {showCharCounter && maxLength && (
                    <>
                        <span
                            className="screen-reader-text"
                            aria-live="polite"
                            role="status"
                            id={`${targetId}-char-counter`}
                        >
                            {charCountScreenReaderText}
                        </span>
                        <p className="sgw-expandable-input__character-counter" aria-hidden>
                            {charLength}/{maxLength}
                        </p>
                    </>
                )}
                <ErrorTag className="sgw-input__error-label" id={`${targetId}-error`} icon={errorIcon}>
                    {error}
                </ErrorTag>
            </div>
        );
    },
);

export default ExpandableInput;
